import { Box, Typography, ToggleButtonGroup, ToggleButton, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useListContext, useTranslate } from 'react-admin';
import { useState } from 'react';
import { Growth } from '@/components/analytics/common/Growth.jsx';
import { Link } from 'react-router-dom';
import { DrawerLink } from '@/components/global/drawers/DrawerLink.jsx';
import { ProductProgressBar } from '@/components/analytics/product/ProductAnalyticsInfo.jsx';
import PropTypes from 'prop-types';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { analyticsStyles } from '@/themes/commonStyles/analytics.js';
import { formatNumberWithComma } from '@/helpers/format/formatNumber.js';
import { Palette } from '@/themes/themeOverrides/Palette';
import { buildDateRangeFilterUrl } from '@/helpers/filters/buildDateRangeFilterUrl.js';

const Title = () => {
    const translate = useTranslate();
    return <Typography variant='h6'>{translate('analytics.collectionPoint.ranking')}</Typography>;
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
    '&.MuiToggleButtonGroup-root': {
        borderRadius: '10px',
    },
    '& .MuiButtonBase-root': {
        fontSize: '11px',
        fontWeight: 600,
        borderRadius: '10px 0 0 10px',
        padding: '7px 14px',
        border: `2px solid ${Palette.gray.gray150}`,
        backgroundColor: Palette.gray.gray50,
        color: Palette.gray.gray700,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: Palette.gray.gray50,
            color: Palette.gray.gray700,
            border: `2px solid ${Palette.gray.gray150}`,
            boxShadow: 'none',
        },
        '& + .MuiButtonBase-root': {
            borderRadius: '0 10px 10px 0',
            borderLeft: `1px solid ${Palette.gray.gray150}`,
        },
        '&.Mui-selected': {
            backgroundColor: Palette.white,
            color: Palette.black,
            boxShadow: Palette.boxShadow.elevation2,
        },
    },
}));

const Switch = ({ value, changeValue }) => {
    const translate = useTranslate();
    return (
        <StyledToggleButtonGroup className='switch' color='standard' value={value} onChange={changeValue} size='small' exclusive>
            <ToggleButton value='transactionsCount'>{translate('menu.transactions')}</ToggleButton>
            <ToggleButton value='transactionsValidContainersCount'>{translate('analytics.collectionPoint.packages')}</ToggleButton>
        </StyledToggleButtonGroup>
    );
};

const StyledElement = styled(Box)(() => ({
    ...analyticsStyles.element,
}));

const Element = ({ item, total }) => {
    if (!item.currentCount) return null;

    const percentage = (item.currentCount / total) * 100;
    const growth = ((item.currentCount - item.previousCount) / item.previousCount) * 100;

    return (
        <StyledElement className='element'>
            <Box className='element-header'>
                <Box className='element-left'>
                    <DrawerLink
                        resource='collection-point'
                        source={item.collectionPointId}
                        action='show'
                        relative
                        label={<Typography variant='caption'>{item.customId}</Typography>}
                    />
                </Box>
                <Box className='element-right'>
                    <Typography variant='caption'>{item.currentCount}</Typography>
                    <Growth value={growth} className='growth--small' />
                </Box>
                {isFinite(percentage) ? (
                    <Typography variant='caption' className='percentage'>
                        {formatNumberWithComma(percentage, 1)}%
                    </Typography>
                ) : null}
            </Box>
            <ProductProgressBar percentage={percentage} />
        </StyledElement>
    );
};

const StyledTransactionCollectionPointRanking = styled(Box)(({ theme }) => ({
    padding: '40px 40px 48px',
    borderRadius: '16px',
    backgroundColor: Palette.white,
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        padding: '24px',
    },
    '& .ranking-header': {
        ...analyticsStyles.rankingHeader,
    },
    '& .ranking-content': {
        marginTop: theme.spacing(2),
    },
    '& .ranking-footer': {
        ...analyticsStyles.rankingFooter,
        '& a': {
            borderRadius: '12px',
        },
    },
    '& .element + .element': {
        marginTop: '17px',
    },
}));

export const TransactionCollectionPointRanking = () => {
    const { data, filterValues } = useListContext();
    const [collectionPointRankingField, setCollectionPointRankingField] = useState('transactionsCount');
    const translate = useTranslate();

    const handleCollectionPointRankingFieldChange = (e, newField) => {
        if (newField) {
            setCollectionPointRankingField(newField);
        }
    };

    if (!data || data.length === 0) return null;
    const analytic = data[0].data;

    const collectionPointRankingData = data.find(s => s.id === 'collectionPointRanking').data;
    const selectedRankingData = collectionPointRankingData[collectionPointRankingField];

    return (
        <StyledTransactionCollectionPointRanking>
            <Box className='ranking-header'>
                <Title />
                <Switch value={collectionPointRankingField} changeValue={handleCollectionPointRankingFieldChange} />
            </Box>
            <Box className='ranking-content'>
                {selectedRankingData.map(item => (
                    <Element key={item.collectionPointId} item={item} total={analytic[collectionPointRankingField]} />
                ))}
            </Box>
            <Box className='ranking-footer'>
                <Button
                    color='secondary'
                    size='medium'
                    component={Link}
                    to={buildDateRangeFilterUrl('collection-point', filterValues, 'analytics.transactionsCount')}
                    variant='contained'
                >
                    {translate('analytics.collectionPoint.seeCollectionPointList')}
                </Button>
            </Box>
        </StyledTransactionCollectionPointRanking>
    );
};

Switch.propTypes = {
    value: PropTypes.oneOf(['transactionsCount', 'transactionsValidContainersCount']).isRequired,
    changeValue: PropTypes.func.isRequired,
};

Element.propTypes = {
    item: PropTypes.shape({
        collectionPointId: PropTypes.string.isRequired,
        customId: PropTypes.string.isRequired,
        previousCount: PropTypes.number.isRequired,
        currentCount: PropTypes.number.isRequired,
    }).isRequired,
    total: PropTypes.number.isRequired,
};
